import { FC, ReactElement } from "react";
import { IconButton, Tooltip, Box, Typography, Divider } from "@mui/material";
import { FiInfo } from "react-icons/fi";
import { parseSelectedOptions, getAdjustmentTypeLabel } from "@/utils/report-utils";
import { numberWithCommas, formatHTSCode } from "@/utils";
import { tokens } from "@/tokens";

interface ReportsInfoTooltipProps {
  report: IReport;
}

const ReportsInfoTooltip: FC<ReportsInfoTooltipProps> = ({ report }): ReactElement => {
  const { selectedOptions, result, entryCount, changeInGoodsValue } = report;

  // Parse selected options to get detailed information
  let parsedOptions = null;
  let adjustmentTypeLabel = "Unknown";
  let adjustmentValue = null;
  let filters = null;
  let dateRange = null;
  let adjustmentDetails = null;

  try {
    if (selectedOptions) {
      parsedOptions = parseSelectedOptions(selectedOptions);
      adjustmentTypeLabel = getAdjustmentTypeLabel(parsedOptions.adjustmentType);
      adjustmentValue = parsedOptions.adjustmentValue;

      // Try to parse as enhanced format for additional details
      let enhancedOptions;
      if (typeof selectedOptions === 'string') {
        enhancedOptions = JSON.parse(selectedOptions);
      } else {
        enhancedOptions = selectedOptions;
      }
      
      if (enhancedOptions && typeof enhancedOptions === 'object' && !Array.isArray(enhancedOptions)) {
        filters = enhancedOptions.filters;
        dateRange = enhancedOptions.dateRange;
        adjustmentDetails = enhancedOptions.adjustmentDetails;
      }
    }
  } catch (error) {
    console.warn('Error parsing selected options:', error);
  }

  const tooltipContent = (
    <Box sx={{ maxWidth: 400, p: 2 }}>
      {/* Adjustment Details */}
      <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1 }}>
        Adjustment Details
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Type:</strong> {adjustmentTypeLabel}
        </Typography>
        
        {/* Value Adjustment Details */}
        {parsedOptions?.adjustmentType === 'value_percentage' && (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Value Adjustment:</strong> {adjustmentDetails?.valuePercentage || adjustmentValue}
            </Typography>
          </>
        )}
        
        {/* Duty Rate Adjustment Details - Show specific changes */}
        {parsedOptions?.adjustmentType === 'duty_rate_adjustment' && adjustmentDetails && (
          <Box sx={{ mt: 1 }}>
            {/* Code Changes */}
            {adjustmentDetails.codeChanges && adjustmentDetails.codeChanges.length > 0 && (
              <Box sx={{ p: 1, bgcolor: tokens.bg.hover, borderRadius: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 0.5 }}>
                  HTS Code Changes ({adjustmentDetails.codeChanges.length}):
                </Typography>
                {adjustmentDetails.codeChanges.slice(0, 5).map((change: any, idx: number) => (
                  <Box key={idx} sx={{ ml: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: tokens.fontSize.lg }}>
                      • {change.originalCode} → {change.targetCode}
                    </Typography>
                    {/* Replace All Mode */}
                    {change.ch99ChangeMode === 'replace_all' && change.newCH99CodeList && change.newCH99CodeList.length > 0 && (
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary }}>
                          - CH99: Replace all with [{change.newCH99CodeList.join(', ')}]
                        </Typography>
                      </Box>
                    )}
                    {/* Selective Mode */}
                    {change.ch99ChangeMode === 'selective' && change.ch99Transformations && change.ch99Transformations.length > 0 && (
                      <Box sx={{ ml: 2 }}>
                        {change.ch99Transformations.map((t: any, tIdx: number) => (
                          <Typography key={tIdx} variant="body2" sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary }}>
                            - CH99: {t.from} → {t.to}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
                {adjustmentDetails.codeChanges.length > 5 && (
                  <Typography variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg, fontStyle: 'italic' }}>
                    ...and {adjustmentDetails.codeChanges.length - 5} more
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Rate Changes */}
            {adjustmentDetails.rateChanges && adjustmentDetails.rateChanges.length > 0 && (
              <Box sx={{ p: 1, bgcolor: tokens.bg.hover, borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 0.5 }}>
                  Duty Rate Changes ({adjustmentDetails.rateChanges.length}):
                </Typography>
                {adjustmentDetails.rateChanges.slice(0, 5).map((change: any, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg }}>
                    • {formatHTSCode(change.htsCode)}: New rate {change.newRate}%
                  </Typography>
                ))}
                {adjustmentDetails.rateChanges.length > 5 && (
                  <Typography variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg, fontStyle: 'italic' }}>
                    ...and {adjustmentDetails.rateChanges.length - 5} more
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
        
        {/* Combined Adjustment Details - Show specific changes */}
        {parsedOptions?.adjustmentType === 'combined_value_and_duty' && adjustmentDetails && (
          <>
            {/* Value Adjustment */}
            <Typography variant="body2" sx={{ mb: 0.5, mt: 0.5 }}>
              <strong>Value Adjustment:</strong> {adjustmentDetails.valuePercentage}
            </Typography>
            
            {/* Code Changes */}
            {adjustmentDetails.codeChanges && adjustmentDetails.codeChanges.length > 0 && (
              <Box sx={{ p: 1, bgcolor: tokens.bg.hover, borderRadius: 1, mt: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 0.5 }}>
                  HTS Code Changes ({adjustmentDetails.codeChanges.length}):
                </Typography>
                {adjustmentDetails.codeChanges.slice(0, 3).map((change: any, idx: number) => (
                  <Box key={idx} sx={{ ml: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: tokens.fontSize.lg }}>
                      • {change.originalCode} → {change.targetCode}
                    </Typography>
                    {/* Replace All Mode */}
                    {change.ch99ChangeMode === 'replace_all' && change.newCH99CodeList && change.newCH99CodeList.length > 0 && (
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary }}>
                          - CH99: Replace all with [{change.newCH99CodeList.join(', ')}]
                        </Typography>
                      </Box>
                    )}
                    {/* Selective Mode */}
                    {change.ch99ChangeMode === 'selective' && change.ch99Transformations && change.ch99Transformations.length > 0 && (
                      <Box sx={{ ml: 2 }}>
                        {change.ch99Transformations.map((t: any, tIdx: number) => (
                          <Typography key={tIdx} variant="body2" sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary }}>
                            - CH99: {t.from} → {t.to}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
                {adjustmentDetails.codeChanges.length > 3 && (
                  <Typography variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg, fontStyle: 'italic' }}>
                    ...and {adjustmentDetails.codeChanges.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Rate Changes */}
            {adjustmentDetails.rateChanges && adjustmentDetails.rateChanges.length > 0 && (
              <Box sx={{ p: 1, bgcolor: tokens.bg.hover, borderRadius: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 0.5 }}>
                  Duty Rate Changes ({adjustmentDetails.rateChanges.length}):
                </Typography>
                {adjustmentDetails.rateChanges.slice(0, 3).map((change: any, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg }}>
                    • {formatHTSCode(change.htsCode)}: New rate {change.newRate}%
                  </Typography>
                ))}
                {adjustmentDetails.rateChanges.length > 3 && (
                  <Typography variant="body2" sx={{ ml: 1, fontSize: tokens.fontSize.lg, fontStyle: 'italic' }}>
                    ...and {adjustmentDetails.rateChanges.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
        
        {dateRange && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Period:</strong> {dateRange.start} to {dateRange.end}
          </Typography>
        )}
      </Box>

      {/* Filters Applied - Only show if there are actual filters */}
      {filters && (
        filters.reconFlag || 
        (filters.htsCodes && filters.htsCodes.length > 0) ||
        (filters.originCodes && filters.originCodes.length > 0) ||
        (filters.manufacturerIds && filters.manufacturerIds.length > 0)
      ) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1 }}>
            Filters Applied
          </Typography>
          <Box sx={{ mb: 2 }}>
            {filters.reconFlag && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Reconciliation Flag:</strong> {filters.reconFlag}
              </Typography>
            )}
            {filters.htsCodes && filters.htsCodes.length > 0 && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>HTS Codes:</strong> {filters.htsCodes.length} selected
              </Typography>
            )}
            {filters.originCodes && filters.originCodes.length > 0 && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Origin Codes:</strong> {filters.originCodes.length} selected
              </Typography>
            )}
            {filters.manufacturerIds && filters.manufacturerIds.length > 0 && (
              <Typography variant="body2">
                <strong>Manufacturer IDs:</strong> {filters.manufacturerIds.length} selected
              </Typography>
            )}
          </Box>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Impact Summary */}
      <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1 }}>
        Impact Summary
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Entries Affected:</strong> {numberWithCommas(entryCount || 0)}
        </Typography>
        
        {/* Value Adjustment - Show value impact */}
        {parsedOptions?.adjustmentType === 'value_percentage' && (
          <Typography variant="body2">
            <strong>Value Impact:</strong> ${numberWithCommas(Number(changeInGoodsValue ?? result ?? 0))}
          </Typography>
        )}
        
        {/* Duty Rate Adjustment - No impact shown */}
        
        {/* Combined Adjustment - Show value impact only */}
        {parsedOptions?.adjustmentType === 'combined_value_and_duty' && (
          <Typography variant="body2">
            <strong>Value Impact:</strong> ${numberWithCommas(Number(changeInGoodsValue ?? result ?? 0))}
          </Typography>
        )}
        
        {/* Fallback for unknown or legacy types */}
        {(!parsedOptions?.adjustmentType || !['value_percentage', 'duty_rate_adjustment', 'combined_value_and_duty'].includes(parsedOptions.adjustmentType)) && (
          <Typography variant="body2">
            <strong>Financial Impact:</strong> ${numberWithCommas(Number(result || 0))}
          </Typography>
        )}
      </Box>

    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="left"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -5],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            backgroundColor: tokens.bg.surface,
            color: tokens.text.primary,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${tokens.border.default}`,
            '& .MuiTooltip-arrow': {
              color: tokens.bg.surface,
              '&::before': {
                border: `1px solid ${tokens.border.default}`,
              },
            },
          },
        },
      }}
    >
      <IconButton 
        disableRipple 
        sx={{ 
          padding: '8px',
          color: '#3b82f6',
          '&:hover': {
            backgroundColor: tokens.status.infoBg,
          },
        }}
      >
        <FiInfo fontSize={16} />
      </IconButton>
    </Tooltip>
  );
};

export default ReportsInfoTooltip;